import { useRef } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { Button, Section, SectionHeading, StickerCard } from "@/components/ui";
import { GOOGLE_REVIEWS, GOOGLE_REVIEWS_LISTING } from "@/data/googleReviews";

function Stars({ count }) {
  return (
    <span className="flex gap-0.5" role="img" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-5 w-5 ${i < count ? "fill-brand-500 text-brand-500" : "text-brand-200"}`}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

function ReviewCard({ review, index }) {
  return (
    <StickerCard tone={index % 2 === 0 ? "white" : "brandTint"} className="flex h-full flex-col gap-3">
      <Stars count={review.stars} />
      <blockquote className="flex-1 text-base leading-relaxed">
        <p>&ldquo;{review.text}&rdquo;</p>
      </blockquote>
      <footer className="mt-auto flex flex-col gap-0.5 border-t border-brand-100 pt-3 text-sm">
        <cite className="font-semibold not-italic text-ink">{review.name}</cite>
        {review.publishAt ? <span className="text-ink/55">Google review · {review.publishAt}</span> : null}
      </footer>
    </StickerCard>
  );
}

export function GoogleReviewsSection() {
  const trackRef = useRef(null);
  const { title, highlight, mapsCta, reviews } = GOOGLE_REVIEWS;

  const scrollByCard = (direction) => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelector(":scope > li");
    const step = slide ? slide.offsetWidth + 12 : Math.round(el.clientWidth * 0.85);
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  };

  return (
    <Section labelledBy="google-reviews-title" testId="home-google-reviews">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
        <SectionHeading id="google-reviews-title" title={title} highlight={highlight} className="sm:max-w-2xl" />
        <div className="flex shrink-0 gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => scrollByCard(-1)}
            aria-label="Previous Google reviews"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => scrollByCard(1)}
            aria-label="Next Google reviews"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-500 text-brand-500 transition-colors hover:bg-brand-500 hover:text-white"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>

      <ul
        ref={trackRef}
        tabIndex={0}
        aria-label="Google reviews, scroll sideways"
        data-testid="google-reviews-carousel"
        className="snap-row -mx-4 mt-8 flex snap-x snap-mandatory scroll-px-4 gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:scroll-px-0 sm:px-0 lg:gap-4"
      >
        {reviews.map((review, index) => (
          <li
            key={review.id}
            className="w-[88%] shrink-0 snap-start sm:w-[calc((100%-0.75rem)/2)] lg:w-[calc((100%-2rem)/3)]"
          >
            <ReviewCard review={review} index={index} />
          </li>
        ))}
      </ul>

      <div className="mt-8 flex justify-center sm:justify-start">
        <Button variant="outline" size="lg" href={GOOGLE_REVIEWS_LISTING.mapsUrl} external>
          {mapsCta}
        </Button>
      </div>
    </Section>
  );
}
